import type { Optional } from '@festina-life/core'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

/**
 * How much further from a tip than it left them a reader may drift before they
 * are taken to have turned away from it. Loose enough to absorb the shake of a
 * hand that has just clicked, tight enough that a tip is not carried around by
 * a reader who has finished with it.
 */
const DRIFT_ALLOWED_AWAY_FROM_A_TIP = 60

type Point = { x: number; y: number }

const distanceTo = (element: Optional<Element>, { x, y }: Point) => {
  if (!element) {
    return Infinity
  }

  const { top, right, bottom, left } = element.getBoundingClientRect()

  return Math.hypot(
    Math.max(left - x, 0, x - right),
    Math.max(top - y, 0, y - bottom),
  )
}

/**
 * Keeps a tip open when it was the tip that moved rather than the reader.
 *
 * A tip a reader can work in can change size under them if its contents are dynamic.
 * A tip anchored above its trigger then grows and shrinks away from a pointer that has
 * not moved at all, and a tip that no longer fits on the side it was on jumps clean
 * across its trigger. The browser reports either as the pointer leaving, which would
 * close the tip out from under thereader who asked for the change.
 *
 * So a close arriving while the tip is resizing is held back, and the reader is
 * given however far the tip just put them to make their way to it. Reaching it
 * hands the tip back to the ordinary hover rules; drifting further away than
 * the tip left them closes it, as leaving a tip always does.
 */
export const useStayOpenWhileResizing = ({
  isHoldingCloseRef,
  isOpen,
  floating,
  reference,
  close,
}: {
  /**
   * Set while a close is being held back, for the caller to consult before it
   * acts on one.
   */
  isHoldingCloseRef: RefObject<boolean>
  isOpen: boolean
  floating: Optional<Element>
  reference: Optional<Element>
  close: () => void
}) => {
  const pointerRef = useRef<Optional<Point>>(undefined)
  const closestReachedRef = useRef(Infinity)

  const distanceFrom = useCallback(
    (point: Point) =>
      Math.min(distanceTo(floating, point), distanceTo(reference, point)),
    [floating, reference],
  )

  useEffect(() => {
    if (!isOpen) {
      isHoldingCloseRef.current = false
      // Where the pointer was last time says nothing about a tip opened since,
      // least of all one opened from the keyboard.
      pointerRef.current = undefined

      return
    }

    const onPointerMove = ({ clientX, clientY }: PointerEvent) => {
      const point = { x: clientX, y: clientY }
      pointerRef.current = point

      if (!isHoldingCloseRef.current) {
        return
      }

      const distance = distanceFrom(point)

      if (distance === 0) {
        // The reader has caught up with the tip, so it is theirs to leave again
        // in the ordinary way.
        isHoldingCloseRef.current = false

        return
      }

      if (
        distance >
        closestReachedRef.current + DRIFT_ALLOWED_AWAY_FROM_A_TIP
      ) {
        isHoldingCloseRef.current = false
        close()

        return
      }

      closestReachedRef.current = Math.min(closestReachedRef.current, distance)
    }

    document.addEventListener('pointermove', onPointerMove)

    return () => document.removeEventListener('pointermove', onPointerMove)
  }, [close, distanceFrom, isHoldingCloseRef, isOpen])

  useEffect(() => {
    if (!floating || !isOpen) {
      return
    }

    // The first callback reports the size the tip opened at, which is not a
    // resize the reader can have been caught out by.
    let hasReportedOpeningSize = false

    const observer = new ResizeObserver(() => {
      if (!hasReportedOpeningSize) {
        hasReportedOpeningSize = true

        return
      }

      const pointer = pointerRef.current

      // With no pointer to have been caught out, there is nothing to hold the
      // tip open for, and nothing that would ever release it again.
      if (!pointer) {
        return
      }

      isHoldingCloseRef.current = true
      // How far the tip has just put the reader, which is the distance they are
      // given to make their way back to it.
      closestReachedRef.current = distanceFrom(pointer)
    })

    observer.observe(floating)

    return () => observer.disconnect()
  }, [distanceFrom, floating, isHoldingCloseRef, isOpen])
}
